import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Package, Users, MessageCircle, DollarSign, ShoppingBag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", description: "", price: 0, image: "", category: "Apparel", in_stock: true };

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // product being edited, or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [subscribers, setSubscribers] = useState([]);

  const loadAll = async () => {
    try {
      const [p, o, s, n] = await Promise.all([
        api.get("/products"),
        api.get("/admin/orders"),
        api.get("/admin/stats"),
        api.get("/admin/newsletter"),
      ]);
      setProducts(p.data.products || []);
      setOrders(o.data.orders || []);
      setStats(s.data);
      setSubscribers(n.data.subscribers || []);
    } catch (e) {
      // silent; ProtectedRoute redirect handles unauthorized
    }
  };

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user]);

  if (loading) return <div className="p-16 text-center font-sports uppercase tracking-widest">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return (
    <div className="p-16 text-center" data-testid="admin-no-access">
      <h1 className="font-sports uppercase text-4xl text-halo-red">403</h1>
      <p className="font-body mt-3 text-halo-navy/70">Admin access only. Signed in as {user.email}.</p>
    </div>
  );

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.id); };
  const closeEditor = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      toast.success(editing === "new" ? "Product created" : "Product updated");
      closeEditor();
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-halo-red font-sports uppercase tracking-[0.3em] text-xs mb-3">Admin Console</div>
        <h1 className="font-sports uppercase tracking-tight text-5xl sm:text-6xl text-halo-navy">
          Halos <span className="text-halo-red">Control Room</span>
        </h1>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-10" data-testid="admin-stats">
            <StatCard label="Revenue" value={`$${stats.revenue_usd.toFixed(2)}`} icon={DollarSign} accent />
            <StatCard label="Paid Orders" value={stats.orders_paid} icon={ShoppingBag} />
            <StatCard label="Products" value={stats.products} icon={Package} />
            <StatCard label="Users" value={stats.users} icon={Users} />
            <StatCard label="Comments" value={stats.comments} icon={MessageCircle} />
          </div>
        )}

        <div className="flex gap-2 mt-12 border-b border-border">
          {[
            { k: "products", label: "Products" },
            { k: "orders", label: "Orders" },
            { k: "newsletter", label: `Newsletter (${subscribers.length})` },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 font-sports uppercase tracking-wider text-xs border-b-2 -mb-px ${
                tab === t.k ? "border-halo-red text-halo-red" : "border-transparent text-halo-navy/60 hover:text-halo-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="mt-8" data-testid="admin-products-tab">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-sports uppercase text-2xl text-halo-navy">All Products ({products.length})</h2>
              <button
                data-testid="admin-new-product-btn"
                onClick={openNew}
                className="inline-flex items-center gap-2 bg-halo-red hover:bg-halo-redHover text-white font-sports uppercase tracking-wider px-5 py-3 text-xs"
              >
                <Plus className="w-4 h-4" strokeWidth={3} /> New Product
              </button>
            </div>

            <div className="overflow-x-auto border border-border">
              <table className="w-full text-left">
                <thead className="bg-halo-navy text-halo-cream">
                  <tr>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Image</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Name</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Category</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Price</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Stock</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-t border-border">
                      <td className="px-4 py-3"><img src={p.image} alt="" className="w-12 h-12 object-cover" /></td>
                      <td className="px-4 py-3 font-sports text-sm uppercase">{p.name}</td>
                      <td className="px-4 py-3 text-xs text-halo-navy/70">{p.category}</td>
                      <td className="px-4 py-3 font-sports">${p.price.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-sports uppercase tracking-wider px-2 py-1 ${
                          p.in_stock ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        }`}>
                          {p.in_stock ? "In Stock" : "Out"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:text-halo-red"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 hover:text-halo-red"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8" data-testid="admin-orders-tab">
            <h2 className="font-sports uppercase text-2xl text-halo-navy mb-6">All Orders ({orders.length})</h2>
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-halo-navy text-halo-cream">
                  <tr>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Date</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Customer</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Amount</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Items</th>
                    <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {orders.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-halo-navy/50 font-editorial italic">No orders yet.</td></tr>
                  ) : orders.map((o) => (
                    <tr key={o.session_id} data-testid={`admin-order-${o.session_id}`} className="border-t border-border">
                      <td className="px-4 py-3 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3">{o.user_email || "guest"}</td>
                      <td className="px-4 py-3 font-sports">${o.amount.toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs">{(o.items || []).map((i) => `${i.name}×${i.qty}`).join(", ")}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-sports uppercase tracking-wider px-2 py-1 ${
                          o.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                        }`}>{o.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {tab === "newsletter" && (
          <div className="mt-8" data-testid="admin-newsletter-tab">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-sports uppercase text-2xl text-halo-navy">Newsletter Subscribers ({subscribers.length})</h2>
              <button
                onClick={() => {
                  const csv = "email,source,created_at\n" + subscribers.map((s) => `${s.email},${s.source || ""},${s.created_at}`).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = "hiti-newsletter.csv"; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="border-2 border-halo-navy text-halo-navy hover:bg-halo-navy hover:text-halo-cream px-5 py-2 font-sports uppercase tracking-wider text-xs"
                data-testid="admin-newsletter-export"
              >
                Export CSV
              </button>
            </div>
            <div className="border border-border bg-white">
              {subscribers.length === 0 ? (
                <div className="p-10 text-center text-halo-navy/50 font-editorial italic">No subscribers yet.</div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-halo-navy text-halo-cream">
                    <tr>
                      <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Email</th>
                      <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Source</th>
                      <th className="px-4 py-3 font-sports uppercase tracking-wider text-xs">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.id} className="border-t border-border">
                        <td className="px-4 py-3 font-body">{s.email}</td>
                        <td className="px-4 py-3 text-xs text-halo-navy/70">{s.source || "—"}</td>
                        <td className="px-4 py-3 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}


        {/* Editor Modal */}
        {editing && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closeEditor}>
            <form
              onSubmit={save}
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-product-form"
              className="bg-white max-w-xl w-full max-h-[90vh] overflow-y-auto border-2 border-halo-navy"
            >
              <div className="bg-halo-navy text-halo-cream p-4 flex items-center justify-between">
                <h3 className="font-sports uppercase tracking-tight text-xl">
                  {editing === "new" ? "New Product" : "Edit Product"}
                </h3>
                <button type="button" onClick={closeEditor} className="hover:text-halo-red"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <Field label="Name" testid="form-name">
                  <Input value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-2" />
                </Field>
                <Field label="Description" testid="form-desc">
                  <Textarea value={form.description} required rows={3} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-2" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Price ($)" testid="form-price">
                    <Input type="number" step="0.01" min="0" value={form.price} required onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none border-2" />
                  </Field>
                  <Field label="Category" testid="form-category">
                    <Input value={form.category} required onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none border-2" />
                  </Field>
                </div>
                <Field label="Image URL" testid="form-image">
                  <Input value={form.image} required onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-none border-2" />
                </Field>
                {form.image && <img src={form.image} alt="preview" className="w-full max-h-48 object-cover border border-border" />}
                <label className="flex items-center gap-2 font-sports uppercase tracking-wider text-xs cursor-pointer">
                  <input type="checkbox" data-testid="form-instock" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} />
                  In Stock
                </label>
              </div>
              <div className="border-t-2 border-halo-navy/20 p-4 flex justify-end gap-2 bg-halo-cream">
                <button type="button" onClick={closeEditor} className="border-2 border-halo-navy text-halo-navy font-sports uppercase tracking-wider px-5 py-2 text-xs">Cancel</button>
                <button type="submit" disabled={saving} data-testid="form-save-btn" className="bg-halo-red hover:bg-halo-redHover text-white font-sports uppercase tracking-wider px-5 py-2 text-xs disabled:opacity-50">
                  {saving ? "…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className={`p-5 border-2 ${accent ? "border-halo-red bg-halo-red/5" : "border-border bg-white"}`}>
    <Icon className={`w-5 h-5 ${accent ? "text-halo-red" : "text-halo-navy/50"}`} strokeWidth={2.5} />
    <div className="font-sports text-3xl mt-2 text-halo-navy">{value}</div>
    <div className="text-[10px] font-sports uppercase tracking-widest text-halo-navy/60 mt-1">{label}</div>
  </div>
);

const Field = ({ label, testid, children }) => (
  <div>
    <Label className="font-sports uppercase tracking-widest text-xs">{label}</Label>
    <div data-testid={testid} className="mt-1">{children}</div>
  </div>
);
